import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import { X, ExternalLink } from "lucide-react";

// Lanza una herramienta (ClinIV/ClinPulse) en una ventana modal con iframe.
// - El botón lanzador es ARRASTRABLE (recuerda su posición) para no estorbar en capturas/lecturas.
// - Móvil/tablet: ventana a pantalla completa. Escritorio: ventana reducida, también arrastrable.
export default function FloatingTool({ src, title, label, Icon, colorClass, shadowClass, bottomClass }) {
  const [open, setOpen] = useState(false);
  const testId = label.toLowerCase();
  const posKey = `float_pos_${testId}`;
  const saved = (() => { try { return JSON.parse(localStorage.getItem(posKey) || "null"); } catch { return null; } })();
  const x = useMotionValue(saved?.x || 0);
  const y = useMotionValue(saved?.y || 0);
  const dragging = useRef(false);
  const savePos = () => localStorage.setItem(posKey, JSON.stringify({ x: x.get(), y: y.get() }));

  useEffect(() => {
    const h = (e) => { if (e.detail === testId) setOpen(true); };
    window.addEventListener("clingo-open", h);
    return () => window.removeEventListener("clingo-open", h);
  }, [testId]);

  return (
    <>
      <motion.button
        drag
        dragMomentum={false}
        dragElastic={0}
        style={{ x, y }}
        onDragStart={() => { dragging.current = true; }}
        onDragEnd={() => { savePos(); setTimeout(() => (dragging.current = false), 40); }}
        onClick={() => { if (dragging.current) return; setOpen(true); }}
        data-testid={`float-${testId}-toggle`}
        title={`${title} · arrástrame`}
        whileTap={{ scale: 0.92 }}
        whileHover={{ scale: 1.06 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={`fixed ${bottomClass} right-6 z-[60] w-14 h-14 hidden md:flex flex-col items-center justify-center rounded-full text-white shadow-xl cursor-grab active:cursor-grabbing ${colorClass} ${shadowClass}`}
      >
        <Icon size={20} />
        <span className="text-[8px] font-extrabold leading-none mt-0.5 tracking-wide">{label}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/50 md:bg-transparent md:pointer-events-none"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
              transition={{ type: "spring", stiffness: 320, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              data-testid={`float-${testId}-window`}
              className="pointer-events-auto bg-white flex flex-col overflow-hidden shadow-2xl
                         fixed inset-0
                         md:inset-auto md:bottom-6 md:right-6 md:w-[420px] md:h-[86vh] md:max-h-[860px] md:rounded-2xl md:border md:border-stone-200"
            >
              <div className="flex items-center justify-between px-4 h-12 bg-brand-ink text-white shrink-0">
                <span className="flex items-center gap-2 font-semibold text-sm"><Icon size={16} /> {title}</span>
                <div className="flex items-center gap-1">
                  <a href={src} target="_blank" rel="noreferrer" title="Abrir en pestaña nueva"
                    className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"><ExternalLink size={16} /></a>
                  <button onClick={() => setOpen(false)} data-testid={`float-${testId}-close`}
                    title="Cerrar" className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"><X size={18} /></button>
                </div>
              </div>
              <iframe src={src} title={title} className="flex-1 w-full border-0" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
